/**
 * Frontend timezone helpers for Pacific Time (America/Los_Angeles).
 *
 * All YOYO activities are PT-local. The browser may be in any locale; we must
 * compute "today in PT", "this weekend in PT", etc. without ever reading
 * Date.toISOString() (UTC) or hard-coding -08:00 / -07:00.
 *
 * Per PRD docs/prd-activities-filter.md §3.2.A.
 */
const PT_TIMEZONE = 'America/Los_Angeles';

const PT_PARTS_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: PT_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  weekday: 'short',
});

const PT_OFFSET_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: PT_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

interface PtDateParts {
  year: number;
  month: number;
  day: number;
  /** 0 = Sunday, 1 = Monday, … 6 = Saturday */
  weekday: number;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/** Decompose an instant into its PT-local year/month/day/weekday parts. */
function ptParts(instant: Date): PtDateParts {
  const parts = PT_PARTS_FORMATTER.formatToParts(instant);
  const lookup: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') lookup[p.type] = p.value;
  }
  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
    weekday: WEEKDAY_INDEX[lookup.weekday] ?? 0,
  };
}

/** Pad to 2 digits. */
function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Format PtDateParts as YYYY-MM-DD. */
function formatPtDate(p: { year: number; month: number; day: number }): string {
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

/** Return today's date in PT as YYYY-MM-DD (independent of browser timezone). */
export function ptToday(now: Date = new Date()): string {
  return formatPtDate(ptParts(now));
}

/**
 * Add a number of calendar days to a PT-local YYYY-MM-DD string and return the
 * resulting YYYY-MM-DD. DST-safe because we operate on date components.
 */
export function ptDateAddDays(dateStr: string, days: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!match) return dateStr;
  // Use UTC math then read components — this avoids the local browser shift.
  const base = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const shifted = new Date(base + days * 24 * 60 * 60 * 1000);
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`;
}

/** Return the last day of the month for a given PT-local YYYY-MM-DD. */
export function ptEndOfMonth(dateStr: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!match) return dateStr;
  const year = Number(match[1]);
  const month = Number(match[2]);
  // Day 0 of next month = last day of current month (UTC math; we only care about components).
  const last = new Date(Date.UTC(year, month, 0));
  return `${last.getUTCFullYear()}-${pad2(last.getUTCMonth() + 1)}-${pad2(last.getUTCDate())}`;
}

/** Return PT offset in minutes for the given instant (negative; e.g. -480 in PST). */
function ptOffsetMinutes(instant: Date): number {
  const parts = PT_OFFSET_FORMATTER.formatToParts(instant);
  const lookup: Record<string, number> = {};
  for (const p of parts) {
    if (p.type === 'literal') continue;
    lookup[p.type] = Number(p.value);
  }
  const asUtc = Date.UTC(
    lookup.year,
    lookup.month - 1,
    lookup.day,
    lookup.hour === 24 ? 0 : lookup.hour,
    lookup.minute,
    lookup.second,
    instant.getUTCMilliseconds()
  );
  return (asUtc - instant.getTime()) / 60000;
}

function parseYmd(dateStr: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** Convert PT wall-clock to UTC instant ms (DST-safe via iteration). */
function ptWallToUtcMs(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  ms: number
): number {
  const naive = Date.UTC(year, month - 1, day, hour, minute, second, ms);
  const offset1 = ptOffsetMinutes(new Date(naive));
  const candidate1 = naive - offset1 * 60000;
  const offset2 = ptOffsetMinutes(new Date(candidate1));
  return offset1 === offset2 ? candidate1 : naive - offset2 * 60000;
}

/** UTC instant (ms) for the start of a PT-local YYYY-MM-DD date. Returns NaN on invalid input. */
export function ptDayStartUtcMs(dateStr: string): number {
  const p = parseYmd(dateStr);
  if (!p) return NaN;
  return ptWallToUtcMs(p.year, p.month, p.day, 0, 0, 0, 0);
}

/** UTC instant (ms) for the end (23:59:59.999) of a PT-local YYYY-MM-DD date. */
export function ptDayEndUtcMs(dateStr: string): number {
  const p = parseYmd(dateStr);
  if (!p) return NaN;
  return ptWallToUtcMs(p.year, p.month, p.day, 23, 59, 59, 999);
}

/** Full PT-local wall-clock components for an instant. */
export interface PtInstantParts {
  year: number;
  month: number; // 1–12
  day: number; // 1–31
  hour: number; // 0–23
  minute: number; // 0–59
  second: number; // 0–59
  ms: number; // 0–999
  /** 0 = Sunday, …, 6 = Saturday */
  weekday: number;
}

/**
 * Decompose an instant into its PT-local Y/M/D/H/m/s/ms components, plus the PT
 * day of week. Used by the recurrence engine to anchor wall-clock time across DST.
 */
export function ptInstantParts(instant: Date): PtInstantParts {
  // Use the offset formatter (includes time-of-day fields).
  const parts = PT_OFFSET_FORMATTER.formatToParts(instant);
  const lookup: Record<string, number> = {};
  for (const p of parts) {
    if (p.type === 'literal') continue;
    lookup[p.type] = Number(p.value);
  }
  // Hour can come back as 24 for midnight-of-next-day in some locales; normalize.
  const hour = lookup.hour === 24 ? 0 : lookup.hour;
  // Reuse weekday from PT_PARTS_FORMATTER which encodes it as a short string.
  const weekdayParts = PT_PARTS_FORMATTER.formatToParts(instant);
  let weekday = 0;
  for (const p of weekdayParts) {
    if (p.type === 'weekday') {
      weekday = WEEKDAY_INDEX[p.value] ?? 0;
      break;
    }
  }
  return {
    year: lookup.year,
    month: lookup.month,
    day: lookup.day,
    hour,
    minute: lookup.minute,
    second: lookup.second,
    ms: instant.getUTCMilliseconds(),
    weekday,
  };
}

/**
 * Convert PT-local Y/M/D/H/m/s/ms components to a UTC `Date`. DST-safe.
 * Used by the recurrence engine to materialize each occurrence's UTC ISO.
 */
export function ptWallToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  ms: number
): Date {
  return new Date(ptWallToUtcMs(year, month, day, hour, minute, second, ms));
}

/**
 * Compute the upcoming weekend's PT-local date range.
 * Returns { from, to } as YYYY-MM-DD inclusive.
 *
 * Algorithm (per PRD §3.2.A):
 *   - Saturday: [today, today+1]
 *   - Sunday:   [today, today]
 *   - Otherwise: [next Saturday, next Sunday]
 */
export function ptThisWeekendRange(now: Date = new Date()): { from: string; to: string } {
  const parts = ptParts(now);
  const todayStr = formatPtDate(parts);
  if (parts.weekday === 6) return { from: todayStr, to: ptDateAddDays(todayStr, 1) }; // Sat
  if (parts.weekday === 0) return { from: todayStr, to: todayStr }; // Sun
  // Mon (1) → +5 to Sat … Fri (5) → +1 to Sat
  const daysUntilSat = (6 - parts.weekday + 7) % 7;
  const sat = ptDateAddDays(todayStr, daysUntilSat);
  return { from: sat, to: ptDateAddDays(sat, 1) };
}
